import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import * as contractsApi from '../../api/contracts'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Badge from '../../components/common/Badge'
import Spinner from '../../components/common/Spinner'
import { calculatePayments } from '../../utils/paymentCalculator'
import { PlusIcon, UserIcon, UserGroupIcon, PencilSquareIcon, DocumentPlusIcon } from '@heroicons/react/24/outline'

/**
 * Contracts Overview Page
 * List of all contracts with filters and actions
 */
export default function ContractsOverview() {
  const { data, isLoading } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => contractsApi.getContracts(),
  })

  const contracts = data?.data || data || []

  const getStatusVariant = (status) => {
    const variants = {
      signed: 'success',
      pending: 'warning',
      draft: 'info',
      rejected: 'danger',
      expired: 'danger',
    }
    return variants[status] || 'gray'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contracts</h1>
          <p className="mt-2 text-gray-600">Manage trainer contracts</p>
        </div>
        <div className="flex gap-2">
          <Link to="/contracts/builder">
            <Button variant="secondary">
              <DocumentPlusIcon className="h-5 w-5 mr-2" />
              Generate from Template
            </Button>
          </Link>
          <Link to="/contracts/new">
            <Button variant="primary">
              <PlusIcon className="h-5 w-5 mr-2" />
              Upload Contract
            </Button>
          </Link>
        </div>
      </div>

      {/* Contracts List */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contract
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trainers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Teams
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Financial Details
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.length > 0 ? (
                contracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contract.title || contract.uid || `Contract #${contract.id}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        {contract.uid}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getStatusVariant(contract.status)}>
                        {contract.status || 'Draft'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      {contract.trainers && contract.trainers.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {contract.trainers.slice(0, 2).map((trainer) => (
                            <div key={trainer.id} className="flex items-center gap-1 text-xs text-gray-600">
                              <UserIcon className="h-3 w-3" />
                              <span>{trainer.first_name} {trainer.last_name}</span>
                            </div>
                          ))}
                          {contract.trainers.length > 2 && (
                            <span className="text-xs text-gray-500">+{contract.trainers.length - 2} more</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No trainers</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {contract.teams && contract.teams.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {contract.teams.slice(0, 2).map((team) => (
                            <div key={team.id} className="flex items-center gap-1 text-xs text-gray-600">
                              <UserGroupIcon className="h-3 w-3" />
                              <span>{team.team_name}</span>
                            </div>
                          ))}
                          {contract.teams.length > 2 && (
                            <span className="text-xs text-gray-500">+{contract.teams.length - 2} more</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No teams</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contract.start_date} - {contract.end_date}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="space-y-1">
                        <div className="font-semibold text-gray-900">
                          €{contract.total_amount?.toLocaleString() || '0'}
                        </div>
                        {contract.payment_frequency && (
                          <>
                            <div className="text-xs text-gray-600">
                              <span className="capitalize">{contract.payment_frequency}</span>
                              {(() => {
                                const payments = calculatePayments(contract);
                                return payments.length > 0 ? (
                                  <span className="ml-1">
                                    ({payments.length} payment{payments.length !== 1 ? 's' : ''})
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            {contract.payment_per_period && (
                              <div className="text-xs text-gray-500">
                                €{contract.payment_per_period.toLocaleString()} / period
                              </div>
                            )}
                            {contract.payment_day_of_month && (
                              <div className="text-xs text-gray-400">
                                Day {contract.payment_day_of_month} of month
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/contracts/${contract.id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          View
                        </Link>
                        <span className="text-gray-300">|</span>
                        <Link
                          to={`/contracts/${contract.id}?tab=payments`}
                          className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1"
                          title="Edit Financial Plan"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          <span className="hidden sm:inline">Edit Plan</span>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No contracts found. Create your first contract to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}